import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../lib/api'
import { splitByLevel } from '../lib/notifications'
import type { ThresholdNotification } from '../lib/types'
import { useWriteErrorHandler } from './useWriteErrorHandler'

export const NOTIFICATIONS_KEY = ['notifications'] as const
const BASE = '/api/v1/notifications'
/** The list, naming every kind this page renders. Without `kind` the server
 * lists threshold rows only, for a page bundle from before MS-868 still open
 * during a rolling deploy (ARCHITECTURE_DECISIONS.md §49). */
export const NOTIFICATIONS_LIST_URL = `${BASE}?kind=threshold&kind=trial_ending`
// The server's cap on one mark-all request (api/schemas/notifications.py).
export const MARK_ALL_MAX_IDS = 500

type Snapshot = { previous: ThresholdNotification[] | undefined }

/** The one source for the top-bar dropdown and the Home notification centre
 * (MS-868, ARCHITECTURE_DECISIONS.md §49): both read the same query, so a
 * change in one shows in the other in the same render.
 *
 * - Refetches when the window regains focus — on this query only; the app's
 *   global default stays off (app/App.tsx). No polling (PO Q17).
 * - Read, unread, mark-all and dismiss are the caller's own state
 *   (notification_reads): each applies optimistically, rolls back and toasts
 *   through the shared write-error handler on failure, and refetches after.
 * - Mark all sends the ids this client holds as unread, so a notification
 *   that arrived after the list was drawn stays unread. */
export function useNotifications() {
  const queryClient = useQueryClient()
  const onWriteError = useWriteErrorHandler()
  const { data } = useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => apiFetch<ThresholdNotification[]>(NOTIFICATIONS_LIST_URL),
    refetchOnWindowFocus: true,
  })

  const items = useMemo(() => data ?? [], [data])
  const { account, user } = useMemo(() => splitByLevel(items), [items])
  const unreadIds = useMemo(() => items.filter((n) => !n.read).map((n) => n.id), [items])

  // Every write: stop an in-flight refetch from overwriting the optimistic
  // state, apply it, and restore the snapshot if the server refuses.
  const optimistic = useCallback(
    async (update: (items: ThresholdNotification[]) => ThresholdNotification[]): Promise<Snapshot> => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_KEY })
      const previous = queryClient.getQueryData<ThresholdNotification[]>(NOTIFICATIONS_KEY)
      queryClient.setQueryData<ThresholdNotification[]>(NOTIFICATIONS_KEY, (old) => update(old ?? []))
      return { previous }
    },
    [queryClient]
  )
  const rollback = useCallback(
    async (error: unknown, _vars: unknown, snapshot: Snapshot | undefined) => {
      queryClient.setQueryData(NOTIFICATIONS_KEY, snapshot?.previous)
      await onWriteError(error)
    },
    [queryClient, onWriteError]
  )
  const settle = useCallback(() => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }), [queryClient])

  const setReadMutation = useMutation({
    mutationFn: ({ id, read }: { id: string; read: boolean }) =>
      apiFetch<ThresholdNotification>(`${BASE}/${id}/read`, { method: 'PUT', body: JSON.stringify({ read }) }),
    onMutate: ({ id, read }) => optimistic((list) => list.map((n) => (n.id === id ? { ...n, read } : n))),
    onError: rollback,
    onSettled: settle,
  })

  const markAllMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      // The endpoint takes at most MARK_ALL_MAX_IDS ids per request.
      let updated = 0
      for (let i = 0; i < ids.length; i += MARK_ALL_MAX_IDS) {
        const chunk = ids.slice(i, i + MARK_ALL_MAX_IDS)
        const result = await apiFetch<{ updated: number }>(`${BASE}/mark-all-read`, {
          method: 'POST',
          body: JSON.stringify({ ids: chunk }),
        })
        updated += result.updated
      }
      return { updated }
    },
    onMutate: (ids) => {
      const marked = new Set(ids)
      return optimistic((list) => list.map((n) => (marked.has(n.id) ? { ...n, read: true } : n)))
    },
    onError: rollback,
    onSettled: settle,
  })

  const dismissMutation = useMutation({
    mutationFn: (id: string) => apiFetch<ThresholdNotification>(`${BASE}/${id}/acknowledge`, { method: 'POST' }),
    onMutate: (id) => optimistic((list) => list.filter((n) => n.id !== id)),
    onError: rollback,
    onSettled: settle,
  })

  const { mutate: setReadMutate } = setReadMutation
  const { mutate: markAllMutate } = markAllMutation
  const { mutate: dismissMutate } = dismissMutation

  const setRead = useCallback((id: string, read: boolean) => setReadMutate({ id, read }), [setReadMutate])
  const markAllRead = useCallback(() => {
    if (unreadIds.length > 0) markAllMutate(unreadIds)
  }, [markAllMutate, unreadIds])
  const dismiss = useCallback((id: string) => dismissMutate(id), [dismissMutate])

  return {
    items,
    account,
    user,
    unreadCount: unreadIds.length,
    setRead,
    markAllRead,
    dismiss,
  }
}

export type NotificationsApi = ReturnType<typeof useNotifications>
